import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BankAccount } from 'src/app/models/bank-account/bank-account';
import { CaseFilter } from 'src/app/models/filters/case-filter';
import { CaseSelectItem } from 'src/app/models/case/case-select-list-item';
import { CustomerFilter } from 'src/app/models/filters/customer-filter';
import { SelectItem } from 'src/app/core/models/responses/select-item';
import { BankAccountService } from 'src/app/services/bank-account.service';
import { DataService } from 'src/app/services/data.service';
import { AlertService } from 'src/app/services/alert.service';

export const BankAccountCurrencyCodes: string[] = ['TRY', 'USD', 'EUR'];
export type BankAccountLinkType = 'case' | 'customer';
export type BankAccountLinkMode = 'existing' | 'new';

@Component({
  selector: 'app-bank-account-create-edit-modal',
  templateUrl: './bank-account-create-edit-modal.component.html',
  styleUrls: ['./bank-account-create-edit-modal.component.scss']
})
export class BankAccountCreateEditModalComponent implements OnInit {

  @Input() isCreate: boolean = true;
  @Input() bankAccountId: number;
  @Input() presetCustomerId: number;
  @Input() presetIban: string;
  @Input() presetCurrencyCode: string;
  @Input() hintText: string;
  isDisabled: boolean = false;
  request: BankAccount = {};
  linkEnabled: boolean = false;
  linkType: BankAccountLinkType = 'case';
  customerMode: BankAccountLinkMode = 'existing';
  currencyCodes = BankAccountCurrencyCodes;
  cases: CaseSelectItem[] = [];
  customers: SelectItem[] = [];
  banks: SelectItem[] = [];
  currentBranch = JSON.parse(localStorage.getItem('currentBranch'));

  constructor(
    private service: BankAccountService,
    private dataService: DataService,
    private alertService: AlertService,
    public activeModal: NgbActiveModal
  ) { }

  ngOnInit(): void {
    // Banka hesabını kasaya bağlama formu: bankaya bağlı kasalar da listelenmeli,
    // aksi halde hâlihazırda bağlı olan kasa kendi düzenleme formundan kaybolur.
    const caseFilter: CaseFilter = { branchId: this.currentBranch.id, includeBankLinkedCases: true };
    this.dataService.getCase(caseFilter).toPromise().then(res => this.cases = res.data);

    const customerFilter: CustomerFilter = { isActive: true };
    this.dataService.getCustomer(customerFilter).toPromise().then(res => this.customers = res.data);

    this.dataService.getBank().toPromise().then(res => this.banks = res.data);

    if (this.isCreate && this.presetCustomerId) {
      this.linkEnabled = true;
      this.linkType = 'customer';
      this.request.customerId = this.presetCustomerId;
    }

    if (this.isCreate && (this.presetIban || this.presetCurrencyCode)) {
      if (this.presetIban) {
        this.request.iban = this.presetIban;
      }
      if (this.presetCurrencyCode) {
        this.request.currencyCode = this.presetCurrencyCode;
      }
    }

    if (!this.isCreate && this.bankAccountId) {
      this.service.find(this.bankAccountId).toPromise().then(res => {
        this.request = res.data;
        this.linkEnabled = this.request.isLinkEnabled ?? !!(this.request.caseId || this.request.customerId);
        this.linkType = this.request.customerId ? 'customer' : 'case';
      });
    }
  }

  linkEnabledChanged() {
    this.request.caseId = undefined;
    this.request.customerId = undefined;
    this.customerMode = 'existing';
    this.clearNewCustomerFields();
    if (!this.linkEnabled) {
      this.linkType = 'case';
    }
  }

  linkTypeChanged() {
    this.request.caseId = undefined;
    this.request.customerId = undefined;
    this.customerMode = 'existing';
    this.clearNewCustomerFields();
  }

  customerModeChanged() {
    this.request.customerId = undefined;
    this.clearNewCustomerFields();
  }

  clearNewCustomerFields() {
    this.request.newCustomerName = undefined;
    this.request.newCustomerDisplayName = undefined;
    this.request.newCustomerPhone = undefined;
  }

  onSubmit() {
    if (!this.isValid()) {
      return;
    }

    this.request.isLinkEnabled = this.linkEnabled;

    if (!this.linkEnabled) {
      this.request.caseId = undefined;
      this.request.customerId = undefined;
      this.clearNewCustomerFields();
    } else if (this.linkType === 'case') {
      this.request.customerId = undefined;
      this.clearNewCustomerFields();
    } else {
      this.request.caseId = undefined;
      if (this.customerMode === 'existing') {
        this.clearNewCustomerFields();
      } else {
        this.request.customerId = undefined;
      }
    }

    this.isDisabled = true;
    if (this.isCreate) {
      this.service.create(this.request).toPromise().then(res => {
        this.isDisabled = false;
        if (res.isSuccess) {
          this.activeModal.close();
          this.alertService.showSuccess('Banka hesabı eklendi.');
          this.service.refreshIndex.next();
        } else {
          this.alertService.showError();
        }
      }).catch(() => this.isDisabled = false);
    } else {
      this.service.update(this.request).toPromise().then(res => {
        this.isDisabled = false;
        if (res.isSuccess) {
          this.activeModal.close();
          this.alertService.showSuccess('Banka hesabı güncellendi.');
          this.service.refreshIndex.next();
        } else {
          this.alertService.showError();
        }
      }).catch(() => this.isDisabled = false);
    }
  }

  isValid(): boolean {
    let messages: string[] = [];
    if (!this.request.bankId)
      messages.push('Banka seçiniz.');
    if (!this.request.currencyCode)
      messages.push('Para birimi seçiniz.');
    if (this.linkEnabled && this.linkType === 'case' && !this.request.caseId)
      messages.push('Kasa seçiniz.');
    if (this.linkEnabled && this.linkType === 'customer' && this.customerMode === 'existing' && !this.request.customerId)
      messages.push('Müşteri seçiniz.');
    if (this.linkEnabled && this.linkType === 'customer' && this.customerMode === 'new' && !this.request.newCustomerName)
      messages.push('Yeni müşteri adı giriniz.');

    if (messages.length > 0) {
      this.alertService.showErrorsToast(messages);
      return false;
    } else return true;
  }
}
